/**
 * FollowUpScaleSection — s128 (canvas "Orella Diagnostic Scales" board D; copy claude/ORELLA_SCALES_COPY_v1.md §4
 * APPROVED). D1: each scale the clinician confirmed, asked again with the same ScoredScale the patient answered in
 * Detect. D2: once a scale is complete, baseline → today with the change in plain words ("Better by 4 points"),
 * following the scale's direction so the patient never has to know which way it runs. Model: utils/followUpScales.js.
 */
import ScoredScale, { scaleTotalText } from '../../components/describe/ScoredScale'
import { useI18n } from '../../i18n'
import { formatDate } from '../../utils/dateUtils'
import { scaleAnswerScore, scaleChange } from '../../utils/followUpScales'

const NAVY = '#15354F'
const BLUE = '#46A8F9'

function ScaleTrend({ def, order, now }) {
  const { t } = useI18n()
  const base = order?.baseline && Number.isFinite(Number(order.baseline.score)) ? Number(order.baseline.score) : null
  const max = Number(def.max) || 1
  const change = scaleChange(def, base, now)
  const h = (v) => `${Math.max(18, Math.round((Number(v) / max) * 110))}px`
  return (
    <div data-scale-trend={def.scale} className="mt-3 rounded-[14px] border border-[#E1EAF1] bg-white p-3">
      <div className="text-[10px] font-extrabold uppercase tracking-[0.1em] text-[#46A8F9]">
        {t('deliver.scaleTrendTitle', { scale: def.scale })}
      </div>
      <div className="flex items-end gap-3.5 py-1.5">
        {base !== null && (
          <div>
            <div className="w-11 rounded-t-lg rounded-b flex justify-center pt-1 text-white font-extrabold text-[12px] tabular-nums" style={{ height: h(base), background: NAVY }}>
              {scaleTotalText(def, base)}
            </div>
            <small className="block text-center text-[10.5px] text-gray-500 mt-1 leading-tight">
              {order.baseline.at ? <>{formatDate(order.baseline.at, { month: 'short', day: 'numeric' })}<br /></> : null}
              {t('deliver.scaleFirstVisit', 'first visit')}
            </small>
          </div>
        )}
        <div>
          <div className="w-11 rounded-t-lg rounded-b flex justify-center pt-1 text-white font-extrabold text-[12px] tabular-nums" style={{ height: h(now), background: BLUE }}>
            {scaleTotalText(def, now)}
          </div>
          <small className="block text-center text-[10.5px] text-gray-500 mt-1 leading-tight">{t('deliver.scaleToday', 'today')}</small>
        </div>
        {change && (
          <div className="pb-8">
            <span className={`text-[12px] font-bold rounded-full px-2.5 py-1 ${change.kind === 'worse' ? 'bg-amber-50 text-amber-800' : change.kind === 'better' ? 'bg-[#E6F6EA] text-[#16A34A]' : 'bg-slate-100 text-slate-600'}`}>
              {change.kind === 'better' ? t('deliver.scaleBetterBy', { n: change.n })
                : change.kind === 'worse' ? t('deliver.scaleWorseBy', { n: change.n })
                : t('deliver.scaleNoChange', 'No change')}
            </span>
          </div>
        )}
      </div>
      <p className="text-[11.5px] text-gray-500 m-0">{t('deliver.scaleTrendNote', 'Your doctor reviews this with your report.')}</p>
    </div>
  )
}

export default function FollowUpScaleSection({ items = [], answers = {}, onChange, doctor = '', tS, disabled = false }) {
  const { t } = useI18n()
  if (!items.length) return null
  return (
    <div data-followup-scales className="flex flex-col gap-3">
      {items.map(({ order, def }) => {
        const now = scaleAnswerScore(def, answers[def.scale])
        return (
          <div key={def.scale} data-followup-scale={def.scale} className="rounded-[14px] border border-[#E1EAF1] bg-white p-3">
            <div className="text-[10px] font-extrabold uppercase tracking-[0.1em] text-[#46A8F9]">
              {tS(def.title || def.scale)}{doctor ? ` · ${t('deliver.scaleAskedBy', { doctor })}` : ''}
            </div>
            <p className="text-[11.5px] text-gray-500 mt-1 mb-2">{t('deliver.scaleSameQuestions', 'Same questions as before.')}</p>
            <ScoredScale question={def} value={answers[def.scale] || ''} onChange={(v) => onChange?.(def.scale, v)} disabled={disabled} tq={tS} to={tS} />
            {now !== null && <ScaleTrend def={def} order={order} now={now} />}
          </div>
        )
      })}
    </div>
  )
}
